import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
} from '@heroui/react';
import { Typography } from '../../../../components';
import { ModelHeaders } from '../../../../api/services/accountsApi';

interface AddHeadersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (headers: ModelHeaders) => Promise<void>;
  accountName: string;
  existingHeaders?: string | null;
}

/**
 * AddHeadersModal Component
 *
 * Modal for adding HTTP headers to OnlyFans model accounts.
 * Users can paste JSON with header values directly.
 */
const AddHeadersModal: React.FC<AddHeadersModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  accountName,
  existingHeaders,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);

  const isEditMode = !!existingHeaders;

  // Загружаем существующие хедеры при открытии модалки
  useEffect(() => {
    if (isOpen && existingHeaders) {
      try {
        // Форматируем JSON для красивого отображения
        const parsed = JSON.parse(existingHeaders);
        setJsonInput(JSON.stringify(parsed, null, 2));
      } catch (error) {
        // Если не удалось распарсить, показываем как есть
        setJsonInput(existingHeaders);
      }
    } else if (isOpen && !existingHeaders) {
      // Очищаем поле при открытии для новых хедеров
      setJsonInput('');
    }
  }, [isOpen, existingHeaders]);

  const handleJsonChange = (value: string) => {
    setJsonInput(value);
    setParseError(null);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setParseError(null);

    try {
      // Parse JSON input
      let headers: ModelHeaders;
      try {
        headers = JSON.parse(jsonInput);
      } catch (error) {
        setParseError('Неверный формат JSON');
        setIsSubmitting(false);
        return;
      }

      // Filter out empty values
      const filteredHeaders = Object.entries(headers).reduce((acc, [key, value]) => {
        if (value && String(value).trim() !== '') {
          acc[key as keyof ModelHeaders] = String(value);
        }
        return acc;
      }, {} as ModelHeaders);

      await onSubmit(filteredHeaders);
      onClose();
      // Reset form
      setJsonInput('');
      setParseError(null);
    } catch (error) {
      console.error('Failed to add headers:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      classNames={{
        base: 'bg-white',
        header: 'border-b border-gray-200',
        body: 'py-6',
        footer: 'border-t border-gray-200',
      }}
    >
      <ModalContent>
        <ModalHeader>
          <Typography variant="h6" className="font-semibold">
            {isEditMode ? `Редактировать Headers для ${accountName}` : `Добавить Headers для ${accountName}`}
          </Typography>
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <Typography variant="body2" className="text-gray-600">
              Вставьте JSON с headers из вашего плагина:
            </Typography>
            <Textarea
              placeholder='{"accept": "...", "cookie": "...", "user-agent": "..."}'
              value={jsonInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleJsonChange(e.target.value)}
              minRows={10}
              classNames={{
                input: 'font-mono text-sm',
                inputWrapper: parseError ? 'border-danger' : '',
              }}
            />
            {parseError && (
              <Typography variant="body3" className="text-danger">
                {parseError}
              </Typography>
            )}
            <Typography variant="body3" className="text-gray-500">
              Пример формата:
              <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
{`{
  "accept": "application/json",
  "cookie": "session=...",
  "user-agent": "Mozilla/5.0..."
}`}
              </pre>
            </Typography>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="default"
            variant="light"
            onPress={onClose}
            disabled={isSubmitting}
          >
            Отмена
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={!jsonInput.trim()}
            className="text-white"
          >
            {isEditMode ? 'Обновить' : 'Добавить'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddHeadersModal;
